import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../shared/branding/OrganizationContext';
import OrgBrandLogo from '../../shared/branding/OrgBrandLogo';
import { isCoursoloBrandName } from '../../shared/branding/brandLogoUtils';
import { serverURL } from '../../constants';
import Header from '../../components/header';

/**
 * Tenant white-label landing — shown on org subdomain / custom domain.
 * Join-only (no platform marketing footer or Create organization CTAs).
 */
export default function TenantLanding() {
    const { t } = useTranslation();
    const { branding, organization } = useOrganization();
    const name = branding?.companyName || organization?.name || t('public.tenantLanding.fallbackName');
    const primary = branding?.primaryColor || '#2563eb';
    const secondary = branding?.secondaryColor || '#0f172a';
    const tagline = branding?.tagline || '';
    const welcomeMessage = branding?.welcomeMessage || '';
    const sso = organization?.sso?.enabled;
    const allowSignup = organization?.settings?.allowSelfSignup !== false;
    const slug = organization?.slug;
    const showTitle = !isCoursoloBrandName(name);
    const supportEmail = branding?.supportEmail || '';

    return (
        <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
            <Header isHome={false} />
            <main className="flex-1">
                <section className="relative overflow-hidden">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(135deg, ${primary}22 0%, ${secondary}10 45%, #f8fafc 100%)`,
                        }}
                    />
                    <div className="relative max-w-4xl mx-auto px-4 pt-28 pb-20 text-center">
                        <OrgBrandLogo branding={branding} className="h-14 mx-auto mb-6 object-contain" alt={name} />
                        {showTitle ? (
                            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white">
                                {name}
                            </h1>
                        ) : (
                            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white">
                                {t('public.tenantLanding.welcome')}
                            </h1>
                        )}
                        {tagline ? (
                            <p className="mt-3 text-xl font-medium text-slate-800 dark:text-slate-200">{tagline}</p>
                        ) : null}
                        <p className="mt-4 text-lg text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
                            {welcomeMessage || t('public.tenantLanding.body')}
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <Link
                                to="/signin"
                                className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-medium text-white shadow-sm"
                                style={{ backgroundColor: primary }}
                            >
                                {t('public.tenantLanding.signIn')}
                            </Link>
                            {sso && (
                                <a
                                    href={`${serverURL}/api/auth/sso/start?slug=${encodeURIComponent(slug || '')}`}
                                    className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-medium border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                >
                                    {t('public.tenantLanding.sso')}
                                </a>
                            )}
                            {allowSignup && (
                                <Link
                                    to="/signup"
                                    className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-medium border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                    style={{ borderColor: `${primary}55`, color: primary }}
                                >
                                    {t('public.tenantLanding.join')}
                                </Link>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <footer className="border-t border-slate-200 dark:border-slate-800 py-6 px-4">
                <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-4 text-sm text-slate-500">
                    <Link to="/privacy" className="hover:text-slate-800 dark:hover:text-slate-200">
                        {t('public.tenantLanding.privacy')}
                    </Link>
                    <Link to="/terms" className="hover:text-slate-800 dark:hover:text-slate-200">
                        {t('public.tenantLanding.terms')}
                    </Link>
                    <Link to="/gdpr" className="hover:text-slate-800 dark:hover:text-slate-200">
                        {t('public.tenantLanding.gdpr')}
                    </Link>
                    {supportEmail ? (
                        <a href={`mailto:${supportEmail}`} className="hover:text-slate-800 dark:hover:text-slate-200">
                            {t('public.tenantLanding.support')}
                        </a>
                    ) : (
                        <Link to="/support" className="hover:text-slate-800 dark:hover:text-slate-200">
                            {t('public.tenantLanding.support')}
                        </Link>
                    )}
                </div>
            </footer>
        </div>
    );
}
